import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { PODCAST_TABLE } from '../../utils/constants';
import { Community, LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';

const PodcastScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    podcastStore: { podcasts, getAllPodcasts, deletePodcast },
    userStore: { communities, user, getCommunities },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [result, setResult] = useState<Row[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const filterPodcasts = useCallback(
    (communities: Community[], ownCommunities: Community[]) => {
      const filtered = communities.filter((community) => {
        return ownCommunities.some((el) => {
          return el.id === community.id;
        });
      });
      const filteredPodcasts = podcasts.filter((podcast) => {
        return filtered.find(({ community }) => community === podcast.publisher);
      });
      return filteredPodcasts;
    },
    [podcasts]
  );

  useEffect(() => {
    const getPodcasts = async () => {
      await getAllPodcasts();

      if (user?.admin) {
        const tempResult: Row[] =
          podcasts.length > 0
            ? podcasts.map((podcast) => ({
                id: podcast.id,
                name: podcast.name,
                value: `${podcast.name} - ${podcast.author}`,
                secondValue: podcast.published ? t('published') : t('draft'),
              }))
            : [];
        setResult(tempResult);
      } else {
        if (communities && user?.communities.length) {
          const filtered = filterPodcasts(communities, user.communities);
          const tempResult: Row[] =
            filtered && filtered.length > 0
              ? filtered.map((podcast) => ({
                  id: podcast.id,
                  name: podcast.name,
                  value: `${podcast.name} - ${podcast.author}`,
                  secondValue: podcast.published ? t('published') : t('draft'),
                }))
              : [];
          setResult(tempResult);
        }
      }
    };

    getPodcasts();
  }, [getAllPodcasts, getCommunities, user, communities, podcasts, t, filterPodcasts]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/podcasts/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/podcasts/create');
  };

  const deleteBook = async () => {
    if (selectedRow?.id) {
      await deletePodcast(selectedRow.id.toString());
      await getAllPodcasts();
      const tempResult: Row[] =
        podcasts.length > 0
          ? podcasts.map((podcast) => ({
              id: podcast.id,
              name: podcast.name,
              value: `${podcast.name} - ${podcast.author}`,
              secondValue: podcast.published ? t('published') : t('draft'),
            }))
          : [];
      setResult(tempResult);
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('podcasts')}>
      <AddButton onClick={navigateTo} text={t('podcasts_add_btn')} />
      <Table
        tableType={PODCAST_TABLE}
        data={result}
        setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
        selectedRow={selectedRow}
        onDelete={(id) => toggleDelete(id)}
      />
      <AlertModal
        open={modalOpen}
        title="podcasts_delete"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteBook}
      />
    </Layout>
  );
});

export default PodcastScreen;
